import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { parseLocaleNum } from "../lib/locale";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Search, Plus, Pencil, Trash2, Loader2, Inbox, X, ChevronRight, Eye, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah, formatTanggal } from "../lib/format";
import { PageHeader } from "../components/ui/page-header";
import { EmptyState } from "../components/ui/empty-state";

const PAGE_SIZE = 10;
const today = () => new Date().toISOString().slice(0, 10);

const emptyItem = { produk_id: "", nama_produk_snapshot: "", target_pcs: 0, harga_per_pcs_sepakat: 0 };
const emptyForm = {
    vendor_id: "", tanggal_spk: today(), deadline: "", items: [{ ...emptyItem }], catatan: "", status: "aktif",
};

const STATUS_TONE = {
    aktif: "bg-emerald-50 text-emerald-700 border-emerald-200",
    selesai: "bg-blue-50 text-blue-700 border-blue-200",
    dibatalkan: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function SPKPage() {
    const nav = useNavigate();
    const { canWrite, canDelete } = useAuth();
    const [items, setItems] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [produkList, setProdukList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [fVendor, setFVendor] = useState("all");
    const [fStatus, setFStatus] = useState("all");
    const [page, setPage] = useState(1);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (fVendor !== "all") params.vendor_id = fVendor;
            if (fStatus !== "all") params.status = fStatus;
            const { data } = await api.get("/spk", { params });
            setItems(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        (async () => {
            try {
                const [v, p] = await Promise.all([api.get("/vendors"), api.get("/master/produk")]);
                setVendors(v.data); setProdukList(p.data);
            } catch (e) { toast.error(errorMessage(e)); }
        })();
    }, []);

    useEffect(() => { const t = setTimeout(() => { load(); setPage(1); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, fVendor, fStatus]);

    const vendorMap = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v])), [vendors]);
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const pageItems = useMemo(() => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [items, page]);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...emptyForm, items: [{ ...emptyItem }] });
        setDialogOpen(true);
    };
    const openEdit = (row) => {
        setEditing(row);
        setForm({
            vendor_id: row.vendor_id, tanggal_spk: row.tanggal_spk, deadline: row.deadline || "",
            items: row.items.map((i) => ({ ...i })), catatan: row.catatan || "", status: row.status,
        });
        setDialogOpen(true);
    };

    const setItem = (idx, patch) => {
        setForm((f) => ({
            ...f,
            items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it),
        }));
    };
    const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
    const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

    const pickProduk = (idx, produk_id) => {
        const p = produkList.find((x) => x.id === produk_id);
        setItem(idx, { produk_id, nama_produk_snapshot: p?.nama_produk || "" });
    };

    const totalTargetPcs = form.items.reduce((s, i) => s + parseLocaleNum(i.target_pcs || 0), 0);
    const totalNilai = form.items.reduce((s, i) => s + parseLocaleNum(i.target_pcs || 0) * parseLocaleNum(i.harga_per_pcs_sepakat || 0), 0);

    const save = async () => {
        if (!form.vendor_id) return toast.error("Vendor wajib dipilih");
        if (!form.tanggal_spk) return toast.error("Tanggal SPK wajib diisi");
        if (form.items.length === 0) return toast.error("Minimal 1 item produk");
        for (const it of form.items) {
            if (!it.produk_id || !it.nama_produk_snapshot) return toast.error("Pilih produk pada setiap item");
            if (parseLocaleNum(it.target_pcs) <= 0) return toast.error("Target pcs harus > 0");
            if (parseLocaleNum(it.harga_per_pcs_sepakat) <= 0) return toast.error("Harga per pcs harus > 0");
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                items: form.items.map((i) => ({
                    produk_id: i.produk_id,
                    nama_produk_snapshot: i.nama_produk_snapshot,
                    target_pcs: parseInt(i.target_pcs),
                    harga_per_pcs_sepakat: parseFloat(i.harga_per_pcs_sepakat),
                })),
            };
            if (editing) {
                await api.put(`/spk/${editing.id}`, payload);
                toast.success("SPK diperbarui");
            } else {
                await api.post("/spk", payload);
                toast.success("SPK ditambahkan");
            }
            setDialogOpen(false); load();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSubmitting(false); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/spk/${confirmDelete.id}`);
            toast.success("SPK dihapus");
            setConfirmDelete(null); load();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setDeleting(false); }
    };

    return (
        <div data-testid="spk-page">
            <PageHeader
                title="SPK / Job Order"
                description="Surat Perintah Kerja produksi ke vendor"
                actions={canWrite() && (
                    <Button onClick={openCreate} data-testid="spk-add-button" className="rounded-lg gap-2 text-white font-medium" style={{ background: "var(--apple-blue)" }}>
                        <Plus className="w-4 h-4" /> Tambah SPK
                    </Button>
                )}
            />

            <div className="rounded-xl bg-white p-4 mb-4" style={{ border: "1px solid var(--border)" }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                        <Input placeholder="Cari nomor SPK..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="spk-search-input" className="pl-9 rounded-lg h-10" />
                    </div>
                    <Select value={fVendor} onValueChange={setFVendor}>
                        <SelectTrigger data-testid="spk-filter-vendor" className="rounded-lg h-10"><SelectValue placeholder="Semua vendor" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua vendor</SelectItem>
                            {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.nama}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={fStatus} onValueChange={setFStatus}>
                        <SelectTrigger data-testid="spk-filter-status" className="rounded-lg h-10"><SelectValue placeholder="Semua status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua status</SelectItem>
                            <SelectItem value="aktif">Aktif</SelectItem>
                            <SelectItem value="selesai">Selesai</SelectItem>
                            <SelectItem value="dibatalkan">Dibatalkan</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-2">
                {loading ? (
                    <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} /></div>
                ) : pageItems.length === 0 ? (
                    <div className="rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
                        <EmptyState icon={FileSignature} title="Belum ada SPK" description="Mulai dengan menambah SPK pertama Anda." testid="spk-empty-mobile" />
                    </div>
                ) : pageItems.map((row) => (
                    <div key={row.id} className="rounded-xl bg-white p-4 cursor-pointer hover:bg-slate-50"
                         style={{ border: "1px solid var(--border)" }} onClick={() => nav(`/spk/${row.id}`)}
                         data-testid={`spk-card-${row.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold text-sm" style={{ color: "var(--apple-blue)" }}>{row.nomor_spk}</div>
                                <div className="text-xs truncate" style={{ color: "var(--text-primary)" }}>{vendorMap[row.vendor_id]?.nama || "-"}</div>
                            </div>
                            <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${STATUS_TONE[row.status]}`}>{row.status}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs pt-2" style={{ borderTop: "1px solid var(--border-soft)", color: "var(--text-muted)" }}>
                            <div>{formatTanggal(row.tanggal_spk)}</div>
                            <div className="text-right num-tabular font-semibold" style={{ color: "var(--text-primary)" }}>{formatRupiah(row.total_nilai_target || 0)}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block rounded-xl overflow-hidden bg-white" style={{ border: "1px solid var(--border)" }}>
                <Table data-testid="spk-data-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Nomor SPK</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Vendor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Tanggal</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-right" style={{ color: "var(--text-muted)" }}>Target Pcs</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-right" style={{ color: "var(--text-muted)" }}>Nilai</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Status</TableHead>
                            <TableHead className="text-right text-xs font-semibold uppercase tracking-wider w-36" style={{ color: "var(--text-muted)" }}>Aksi</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7} className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: "var(--apple-blue)" }} /></TableCell></TableRow>
                        ) : pageItems.length === 0 ? (
                            <TableRow><TableCell colSpan={7}><EmptyState icon={FileSignature} title="Belum ada SPK" description="Mulai dengan menambah SPK pertama Anda." testid="spk-empty-desktop" /></TableCell></TableRow>
                        ) : pageItems.map((row) => (
                            <TableRow key={row.id} data-testid={`spk-row-${row.id}`} className="cursor-pointer" onClick={() => nav(`/spk/${row.id}`)}>
                                <TableCell className="font-semibold" style={{ color: "var(--apple-blue)" }}><div className="flex items-center gap-1.5">{row.nomor_spk}<ChevronRight className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} /></div></TableCell>
                                <TableCell className="text-sm" style={{ color: "var(--text-primary)" }}>{vendorMap[row.vendor_id]?.nama || "-"}</TableCell>
                                <TableCell className="text-sm" style={{ color: "var(--text-muted)" }}>{formatTanggal(row.tanggal_spk)}</TableCell>
                                <TableCell className="text-sm text-right num-tabular" style={{ color: "var(--text-primary)" }}>{parseLocaleNum(row.total_target_pcs || 0).toLocaleString("id-ID")}</TableCell>
                                <TableCell className="text-sm font-semibold text-right num-tabular" style={{ color: "var(--text-primary)" }}>{formatRupiah(row.total_nilai_target || 0)}</TableCell>
                                <TableCell><span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${STATUS_TONE[row.status]}`}>{row.status}</span></TableCell>
                                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center justify-end gap-1">
                                        <button onClick={() => nav(`/spk/${row.id}`)} className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]" style={{ color: "var(--text-secondary)" }} title="Detail"><Eye className="w-4 h-4" /></button>
                                        {canWrite() && <button onClick={() => openEdit(row)} data-testid={`spk-edit-btn-${row.id}`} className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]" style={{ color: "var(--text-secondary)" }} title="Edit"><Pencil className="w-4 h-4" /></button>}
                                        {canDelete() && <button onClick={() => setConfirmDelete(row)} data-testid={`spk-delete-btn-${row.id}`} className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)]" style={{ color: "var(--apple-red)" }} title="Hapus"><Trash2 className="w-4 h-4" /></button>}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {!loading && items.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: "1px solid var(--border)", background: "var(--bg-subtle)" }}>
                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>Menampilkan {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, items.length)} dari {items.length} data</div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg h-8">Sebelumnya</Button>
                            <span className="text-xs px-2" style={{ color: "var(--text-secondary)" }}>Hal {page} / {totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded-lg h-8">Selanjutnya</Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="spk-modal-dialog" className="md:rounded-2xl md:max-w-3xl md:max-h-[90vh] md:overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit SPK" : "Tambah SPK / Job Order"}</DialogTitle>
                        <DialogDescription>Nomor SPK di-generate otomatis. Satu SPK boleh multi produk.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="sm:col-span-2">
                                <Label>Vendor <span className="text-rose-600">*</span></Label>
                                <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })} disabled={!!editing}>
                                    <SelectTrigger data-testid="spk-form-vendor" className="mt-1.5 rounded-xl"><SelectValue placeholder="Pilih vendor" /></SelectTrigger>
                                    <SelectContent>
                                        {vendors.filter((v) => v.status === "aktif").map((v) => <SelectItem key={v.id} value={v.id}>{v.kode_vendor} · {v.nama}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="aktif">Aktif</SelectItem>
                                        <SelectItem value="selesai">Selesai</SelectItem>
                                        <SelectItem value="dibatalkan">Dibatalkan</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Tanggal SPK <span className="text-rose-600">*</span></Label>
                                <Input type="date" value={form.tanggal_spk} onChange={(e) => setForm({ ...form, tanggal_spk: e.target.value })} data-testid="spk-form-tanggal" className="mt-1.5 rounded-xl" />
                            </div>
                            <div>
                                <Label>Deadline (opsional)</Label>
                                <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="mt-1.5 rounded-xl" />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label>Item Produk <span className="text-rose-600">*</span></Label>
                                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="spk-form-add-item" className="rounded-lg h-8 gap-1"><Plus className="w-3.5 h-3.5" /> Tambah Item</Button>
                            </div>
                            <div className="space-y-2">
                                {form.items.map((it, idx) => (
                                    <div key={idx} data-testid={`spk-form-item-${idx}`} className="grid grid-cols-12 gap-2 p-3 rounded-xl bg-slate-50/70 border border-slate-200/60">
                                        <div className="col-span-12 sm:col-span-5">
                                            <div className="text-[10px] uppercase text-slate-500 mb-1">Produk</div>
                                            <Select value={it.produk_id} onValueChange={(v) => pickProduk(idx, v)}>
                                                <SelectTrigger className="rounded-lg h-9 bg-white"><SelectValue placeholder="Pilih produk" /></SelectTrigger>
                                                <SelectContent>
                                                    {produkList.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama_produk}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="col-span-6 sm:col-span-3">
                                            <div className="text-[10px] uppercase text-slate-500 mb-1">Target Pcs</div>
                                            <Input type="text" inputMode="decimal" value={it.target_pcs} onChange={(e) => setItem(idx, { target_pcs: e.target.value })} className="rounded-lg h-9 bg-white" />
                                        </div>
                                        <div className="col-span-6 sm:col-span-3">
                                            <div className="text-[10px] uppercase text-slate-500 mb-1">Harga/Pcs (Rp)</div>
                                            <Input type="text" inputMode="decimal" value={it.harga_per_pcs_sepakat} onChange={(e) => setItem(idx, { harga_per_pcs_sepakat: e.target.value })} className="rounded-lg h-9 bg-white" />
                                        </div>
                                        <div className="col-span-12 sm:col-span-1 flex sm:justify-center sm:items-end">
                                            {form.items.length > 1 && (
                                                <button type="button" onClick={() => removeItem(idx)} className="text-rose-600 hover:bg-rose-50 p-2 rounded-lg h-9" title="Hapus item"><X className="w-4 h-4" /></button>
                                            )}
                                        </div>
                                        <div className="col-span-12 text-right text-xs text-slate-500 num-tabular">
                                            Subtotal: <span className="font-semibold text-slate-800">{formatRupiah(parseLocaleNum(it.target_pcs || 0) * parseLocaleNum(it.harga_per_pcs_sepakat || 0))}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-3 flex items-center justify-end gap-6 text-sm">
                                <div className="text-slate-600">Total Target: <span className="font-bold text-slate-900 num-tabular">{totalTargetPcs.toLocaleString("id-ID")} pcs</span></div>
                                <div className="text-slate-600">Total Nilai: <span className="font-bold text-teal-700 num-tabular">{formatRupiah(totalNilai)}</span></div>
                            </div>
                        </div>

                        <div>
                            <Label>Catatan</Label>
                            <Textarea value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} className="mt-1.5 rounded-xl" rows={2} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Batal</Button>
                        <Button onClick={save} disabled={submitting} data-testid="spk-modal-save-button" className="rounded-lg text-white" style={{ background: "var(--apple-blue)" }}>
                            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan SPK"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="md:rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus SPK</AlertDialogTitle>
                        <AlertDialogDescription>Yakin ingin menghapus <span className="font-semibold text-slate-900">{confirmDelete?.nomor_spk}</span>? SPK tidak dapat dihapus jika sudah punya transaksi terkait.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-lg">Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} disabled={deleting} data-testid="confirm-delete-button" className="rounded-lg text-white" style={{ background: "var(--apple-red)" }}>{deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menghapus...</> : "Hapus"}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
